import { useEffect, useRef, useState } from 'react';
import CEProductSelector from './CEProductSelector';
import CEProductPreviewer from './CEProductPreviewer';
import { CEProductCustomizationForm } from '../product-customization-form/CEProductCustomizationForm';
import CEAddToCartButton from '../add-to-cart-button/CEAddToCartButton';
import { useCEProductSelector } from '../../bitf-customization-engine/useCEProductSelector';
import { useCEProductCustomization } from '../../bitf-customization-engine/useCEProductCustomization';
import { CEProductCustomizationPreview } from '../../bitf-customization-engine/CEProductCustomizationPreview';
import CEProductCollectionSelector from './CEProductCollectionSelector';
import { useTranslate } from '../../hooks/useTranslate';
import CEProductCustomizationPrice from '../product-customization-price/CEProductCustomizationPrice';
import DisplayAt from '../../components/DisplayAt';

export default function CEQuickBuy({ localization, containerWidth, sizeChartModalId, attentionMessage }) {
  const { translate } = useTranslate(localization?.locale);
  const [customize, setCustomize] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const mobileDrawerId = 'quick-buy-customization-drawer';
  const mobileFormRef = useRef(null);

  // Product customization hook
  const { setStore, resetCustomization, store } = useCEProductCustomization({
    storeName: 'quick-buy',
    watchStoreChanges: true,
    localization,
  });

  const { currentProductVariant, isLoadingCustomization } = store || {};

  // Product selector hook
  const productSelectorHook = useCEProductSelector({ localization });
  const { selectedProduct, selectedVariant } = productSelectorHook;

  // Reset function for after successful add to cart
  const handleAddToCartSuccess = () => {
    // Reset customization store
    resetCustomization();

    // Reset currentProductHandle separately since it's not included in resetCustomization
    setStore({
      currentProductHandle: null,
    });

    setIsMobileDrawerOpen(false);
    setCustomize(false);
  };

  useEffect(() => {
    if (selectedProduct?.handle) {
      setStore({ currentProductHandle: selectedProduct?.handle });
    }
  }, [selectedProduct, setStore]);

  useEffect(() => {
    // Prevent background scrolling when drawer is open
    if (isMobileDrawerOpen) {
      document.body.style.overflow = 'hidden';
      // Scroll form to top when drawer opens
      if (mobileFormRef.current) {
        mobileFormRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    if (selectedVariant && isLoadingCustomization === false && currentProductVariant?.id !== selectedVariant.id) {
      setStore({ currentProductVariant: selectedVariant });
    }
  }, [selectedVariant, setStore, isLoadingCustomization, currentProductVariant]);

  return (
    <>
      <div className="quick-buy-wrapper" aria-busy={isLoadingCustomization ? 'true' : 'false'}>
        <div className={`quick-buy-container container container--${containerWidth}`}>
          <div className="quick-buy-previewer-box">
            <div className="md:hidden">
              <p className={`PrimaryFont Body--M container container--${containerWidth} Uppercase`}>
                {translate('customization.form.choose_official_customization')}
              </p>
            </div>

            <DisplayAt value="mobile">
              {/* Collections Selector Mobile*/}
              <div className="md:hidden">
                <CEProductCollectionSelector
                  productSelectorHook={productSelectorHook}
                  localization={localization}
                  resetCustomization={resetCustomization}
                />
              </div>
            </DisplayAt>

            {/* Main Image (back) */}
            {customize && (
              <div
                className={`quick-buy-previewer-box__customization-preview hidden  ${customize ? 'md:flex' : ''} bg-grey-ce-image aspect-square`}
              >
                <CEProductCustomizationPreview
                  customizationHookConfig={{
                    storeName: 'quick-buy',
                    watchStoreChanges: false,
                    language: localization?.locale,
                  }}
                  showProductImage={true}
                  mixBlendMode={true}
                  productVariant={selectedVariant}
                />
              </div>
            )}

            {/* Main Image (front) */}
            {!customize && (
              <div className={`${customize ? 'md:hidden' : ''} bg-grey-ce-image aspect-square`}>
                <CEProductPreviewer selectedProduct={selectedProduct} selectedVariant={selectedVariant} />
              </div>
            )}

            <DisplayAt value="desktop">
              {/* Collections Selector */}
              <div className="hidden md:flex product-customization__collection">
                <CEProductCollectionSelector
                  productSelectorHook={productSelectorHook}
                  localization={localization}
                  resetCustomization={resetCustomization}
                />
              </div>
            </DisplayAt>
          </div>

          <DisplayAt value="desktop">
            <div className={`quick-buy-form-box hidden md:flex`}>
              <div className="quick-buy-form-container">
                {/* Product Selector */}
                <div className="quick-buy-form-content bg-slate-150">
                  {/* Toggle Customize */}
                  <div className="quick-buy-form-content__header bg-slate-150 hidden md:flex">
                    <p className="PrimaryFont Body--M Uppercase">
                      {translate('customization.form.choose_official_customization')}
                    </p>
                  </div>
                  <CEProductSelector
                    productSelectorHook={productSelectorHook}
                    localization={localization}
                    sizeChartModalId={sizeChartModalId}
                  />
                  {/* Toggle Customize */}
                  <div className="quick-buy-form-content__header bg-slate-150">
                    <label htmlFor="customize_switch" className="switch-wrapper Body--2XS Semibold text-blue">
                      <input
                        type="checkbox"
                        class="switch"
                        role="switch"
                        id="customize_switch"
                        checked={customize}
                        onChange={(e) => {
                          resetCustomization();
                          setCustomize(e.target.checked);
                          if (!e.target.checked) {
                            setIsMobileDrawerOpen(false);
                          }
                        }}
                      />
                      {translate('customization.form.customize')}
                    </label>
                  </div>

                  <div className={`quick-buy-form-container__form`}>
                    <CEProductCustomizationForm
                      customizationHookConfig={{
                        storeName: 'quick-buy',
                        watchStoreChanges: false,
                        language: localization?.locale,
                      }}
                      showToggle={false}
                      isCustomize={customize}
                    />
                  </div>

                  <div className="quick-buy-form-container__footer">
                    <div className="quick-buy-form-container__footer-price gap-2">
                      <CEProductCustomizationPrice
                        storeName={'quick-buy'}
                        language={localization?.locale}
                        locale={localization?.locale}
                        showOnlyPrice={true}
                        extraPriceClassName={'PrimaryFont Body--M text-blue'}
                        extraCompareAtPriceClassName={'PrimaryFont Body--M Regular line-through text-slate-600'}
                      />
                    </div>
                    <CEAddToCartButton
                      storeName={'quick-buy'}
                      onAddToCartSuccess={handleAddToCartSuccess}
                      language={localization?.locale}
                    />
                  </div>
                </div>

                {/* Add to Cart Button */}
                <div className="bg-slate-150 d-flex flex-column">
                  <p className="Label--M Semibold SecondaryFont quick-buy-form-container__footer-label">
                    {attentionMessage || translate('customization.form.attention_message')}
                  </p>
                </div>
              </div>
            </div>
          </DisplayAt>
          <DisplayAt value="mobile">
            <div className="w-full customize-button-mobile">
              <button
                type="button"
                className="om-button button w-full button-primary product-customization-drawer-pdp__open-button"
                aria-controls={mobileDrawerId}
                disabled={!selectedVariant}
                onClick={() => {
                  setIsMobileDrawerOpen(true);
                  setCustomize(true);
                }}
              >
                <span className="om-button__label PrimaryFont Label--L Medium">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6.00192 2.65546C6.36925 2.65546 6.66889 2.95513 6.66891 3.32245C6.66891 3.6898 6.36927 3.98944 6.00192 3.98944H4.66891C2.83092 3.98944 1.33395 5.48641 1.33395 7.32441V15.3273C1.33395 17.1653 2.83092 18.6623 4.66891 18.6623H12.6718C14.5098 18.6623 16.0068 17.1653 16.0068 15.3273V13.9934C16.0069 13.6261 16.3065 13.3264 16.6738 13.3264C17.0409 13.3266 17.3407 13.6262 17.3408 13.9934V15.3273C17.3408 17.9013 15.2458 19.9962 12.6718 19.9963H4.66891C2.09496 19.9963 -3.05176e-05 17.9013 -3.05176e-05 15.3273V7.32441C-3.05176e-05 4.75045 2.09496 2.65546 4.66891 2.65546H6.00192ZM15.7334 0.00506939L15.7363 0.00799908C17.9978 0.194463 19.8049 2.00546 19.9951 4.26679C20.0289 4.66393 19.8882 5.05707 19.6084 5.3371L12.041 12.9045C10.9068 14.0374 9.40001 14.6612 7.79684 14.6613H7.33884C6.23569 14.6611 5.33884 13.7635 5.33884 12.6603V12.2033C5.33885 10.6 5.96242 9.09144 7.09567 7.95819L14.6621 0.391788C14.9421 0.107815 15.3361 -0.0287999 15.7334 0.00506939ZM8.03708 8.90155C7.15658 9.7807 6.66998 10.9559 6.66989 12.2023V12.6603C6.67015 13.0275 6.9697 13.3264 7.33688 13.3264H7.79391C9.04039 13.3263 10.2142 12.8397 11.0947 11.9592L18.6806 4.37226L18.6142 4.02655C18.3577 2.68634 17.3108 1.6388 15.9707 1.38202L15.624 1.31562L8.03708 8.90155Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="PrimaryFont Medium Label--L">{translate('customization.form.customize')}</span>
                </span>
                <span className="om-button__icon-wrapper">
                  <svg
                    width="20"
                    className="icon icon-carousel-arrow-right"
                    viewBox="0 0 19 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.9766 9.64258L18.335 9.99805L17.9766 10.3535L9.16406 19.085L8.46094 18.374L16.4082 10.498H0V9.49805H16.4082L8.46094 1.62109L8.8125 1.2666L9.16406 0.911133L17.9766 9.64258Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </DisplayAt>
        </div>
        <DisplayAt value="mobile">
          <div
            id={mobileDrawerId}
            className={`product-customization-drawer product-customization-drawer-quick-buy ${isMobileDrawerOpen ? 'product-customization-drawer--open' : 'product-customization-drawer--closed'}`}
          >
            <div className="product-customization-drawer__container">
              {/* Close button */}
              <div className="product-customization-drawer__header">
                <button
                  onClick={() => {
                    setIsMobileDrawerOpen(false);
                    setCustomize(false);
                  }}
                  className="tap-area"
                  aria-label="Close drawer"
                >
                  <svg width="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1.55529 0.848033C1.82865 0.574666 2.27215 0.574666 2.54551 0.848033L7.00015 5.30267L11.4548 0.848033C11.7281 0.574667 12.1716 0.574666 12.445 0.848033L13.1521 1.55514C13.4255 1.82851 13.4255 2.272 13.1521 2.54537L8.69748 7L13.1521 11.4546C13.4255 11.728 13.4255 12.1715 13.1521 12.4449L12.445 13.152C12.1716 13.4253 11.7281 13.4253 11.4548 13.152L7.00015 8.69733L2.54551 13.152C2.27215 13.4253 1.82865 13.4253 1.55529 13.152L0.848181 12.4449C0.574813 12.1715 0.574814 11.728 0.848181 11.4546L5.30282 7L0.848181 2.54537C0.574814 2.272 0.574813 1.82851 0.848181 1.55514L1.55529 0.848033Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </div>
              <div className="product-customization-drawer__product-preview bg-grey-ce-image">
                <div className="product-customization-drawer__product-preview-content">
                  <CEProductCustomizationPreview
                    customizationHookConfig={{
                      storeName: 'quick-buy',
                      watchStoreChanges: false,
                      language: localization?.locale,
                    }}
                    showProductImage={true}
                    mixBlendMode={true}
                  />
                </div>
              </div>

              <div className="product-customization-drawer__form" ref={mobileFormRef}>
                <CEProductSelector
                  productSelectorHook={productSelectorHook}
                  localization={localization}
                  sizeChartModalId={sizeChartModalId}
                />
                <CEProductCustomizationForm
                  customizationHookConfig={{
                    storeName: 'quick-buy',
                    watchStoreChanges: false,
                    language: localization?.locale,
                  }}
                  showToggle={false}
                  isCustomize={customize}
                />

                <p className="Label--M Semibold SecondaryFont quick-buy-form-container__footer-label-drawer">
                  {attentionMessage || translate('customization.form.attention_message')}
                </p>
              </div>

              <div className="product-customization-drawer__footer d-flex gap-2">
                <CEAddToCartButton
                  selectedProduct={selectedProduct}
                  selectedVariant={selectedVariant}
                  storeName={'quick-buy'}
                  onAddToCartSuccess={handleAddToCartSuccess}
                  language={localization?.locale}
                  extraClassName="w-full"
                >
                  <CEProductCustomizationPrice
                    storeName={'quick-buy'}
                    language={localization?.locale}
                    locale={localization?.locale}
                    showOnlyPrice={true}
                    selectedVariant={selectedVariant}
                    currencyCode={selectedProduct?.currency_code || 'EUR'}
                    extraPriceClassName="Body--XS text-end PrimaryFont"
                    extraCompareAtPriceClassName="Body--XS line-through text-end PrimaryFont"
                  />
                </CEAddToCartButton>
              </div>
            </div>
          </div>
        </DisplayAt>
        {isLoadingCustomization && <div aria-hidden="true" className="quick-buy-loading-overlay" />}
      </div>
    </>
  );
}
